import { useEffect, useRef, useState, useCallback } from "react";
import { Crop as CropIcon, RotateCcw, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

const aspectFor = (orientation) => (orientation === "portrait" ? 9 / 16 : 16 / 9); // width/height

// Largest rect with the target aspect that fits inside natW x natH, centered.
function maxRect(natW, natH, aspect) {
  let w = natW;
  let h = w / aspect;
  if (h > natH) {
    h = natH;
    w = h * aspect;
  }
  return { x: (natW - w) / 2, y: (natH - h) / 2, width: w, height: h };
}

export function CropEditor({ src, orientation, crop, onChange }) {
  const aspect = aspectFor(orientation);
  const containerRef = useRef(null);
  const [nat, setNat] = useState(null); // {w,h}
  const [rect, setRect] = useState(null); // natural px
  const [mode, setMode] = useState(crop ? "custom" : "auto");
  const [sizePct, setSizePct] = useState(100);
  const rectRef = useRef(null);
  const natRef = useRef(null);
  useEffect(() => { rectRef.current = rect; }, [rect]);
  useEffect(() => { natRef.current = nat; }, [nat]);

  const onImgLoad = (e) => {
    const w = e.target.naturalWidth;
    const h = e.target.naturalHeight;
    setNat({ w, h });
    if (crop && crop.width) {
      setRect({ x: crop.x, y: crop.y, width: crop.width, height: crop.height });
      setMode("custom");
      const mr = maxRect(w, h, aspect);
      setSizePct(Math.round((crop.width / mr.width) * 100));
    } else {
      const mr = maxRect(w, h, aspect);
      setRect(mr);
      setMode("auto");
      setSizePct(100);
    }
  };

  const commit = useCallback(
    (r, m) => {
      if (m === "auto") {
        onChange(null); // auto centred crop → omit crop values
      } else if (nat) {
        onChange({
          x: Math.round(r.x),
          y: Math.round(r.y),
          width: Math.round(r.width),
          height: Math.round(r.height),
          originalImageWidth: nat.w,
          originalImageHeight: nat.h,
        });
      }
    },
    [nat, onChange],
  );

  const applySize = (pct) => {
    if (!nat) return;
    const mr = maxRect(nat.w, nat.h, aspect);
    const width = (mr.width * pct) / 100;
    const height = width / aspect;
    const cx = rect ? rect.x + rect.width / 2 : nat.w / 2;
    const cy = rect ? rect.y + rect.height / 2 : nat.h / 2;
    let x = cx - width / 2;
    let y = cy - height / 2;
    x = Math.max(0, Math.min(x, nat.w - width));
    y = Math.max(0, Math.min(y, nat.h - height));
    const next = { x, y, width, height };
    setRect(next);
    setSizePct(pct);
    setMode("custom");
    commit(next, "custom");
  };

  const startDrag = (e) => {
    const container = containerRef.current;
    const r0 = rectRef.current;
    const n = natRef.current;
    if (!container || !n || !r0) return;
    e.preventDefault();
    const bounds = container.getBoundingClientRect();
    const p0 = e.touches ? e.touches[0] : e;
    const d = { startX: p0.clientX, startY: p0.clientY, rx: r0.x, ry: r0.y, w: r0.width, h: r0.height, sx: n.w / bounds.width, sy: n.h / bounds.height };

    const move = (ev) => {
      if (ev.cancelable) ev.preventDefault();
      const p = ev.touches ? ev.touches[0] : ev;
      let x = d.rx + (p.clientX - d.startX) * d.sx;
      let y = d.ry + (p.clientY - d.startY) * d.sy;
      x = Math.max(0, Math.min(x, n.w - d.w));
      y = Math.max(0, Math.min(y, n.h - d.h));
      setRect({ x, y, width: d.w, height: d.h });
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
      setMode("custom");
      if (rectRef.current) commit(rectRef.current, "custom");
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
  };

  const autoCenter = () => {
    if (!nat) return;
    const mr = maxRect(nat.w, nat.h, aspect);
    setRect(mr);
    setSizePct(100);
    setMode("auto");
    commit(mr, "auto");
  };

  const pct = (v) => `${v}%`;
  const overlay = rect && nat
    ? { left: pct((rect.x / nat.w) * 100), top: pct((rect.y / nat.h) * 100), width: pct((rect.width / nat.w) * 100), height: pct((rect.height / nat.h) * 100) }
    : null;

  return (
    <div className="space-y-4" data-testid="crop-editor">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-re-charcoal">
          <CropIcon className="h-4 w-4 text-re-red" /> Uitsnede · {orientation === "portrait" ? "9:16 (verticaal)" : "16:9 (horizontaal)"}
        </span>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${mode === "auto" ? "bg-re-soft text-re-muted" : "bg-re-red/10 text-re-red"}`} data-testid="crop-mode">
          {mode === "auto" ? "Automatisch" : "Aangepast"}
        </span>
      </div>

      <div ref={containerRef} className="relative w-full select-none overflow-hidden rounded-xl border border-re-line bg-black/5">
        <img src={src} alt="Uitsnede" onLoad={onImgLoad} className="block w-full" draggable={false} />
        {overlay && (
          <div
            onMouseDown={startDrag}
            onTouchStart={startDrag}
            data-testid="crop-rect"
            className="absolute cursor-move border-2 border-re-red"
            style={{ ...overlay, boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)" }}
          >
            <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 opacity-40">
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="border border-white/40" />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-re-muted">
          <span>Grootte uitsnede</span>
          <span>{sizePct}%</span>
        </div>
        <Slider value={[sizePct]} min={20} max={100} step={1} onValueChange={(v) => applySize(v[0])} data-testid="crop-size-slider" />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={autoCenter} data-testid="crop-auto-center" className="rounded-full">
          <Wand2 className="mr-1.5 h-4 w-4" /> Automatisch centreren
        </Button>
        <Button size="sm" variant="outline" onClick={() => applySize(sizePct)} data-testid="crop-adjust" className="rounded-full">
          <CropIcon className="mr-1.5 h-4 w-4" /> Uitsnede aanpassen
        </Button>
        <Button size="sm" variant="ghost" onClick={autoCenter} data-testid="crop-reset" className="rounded-full text-re-muted">
          <RotateCcw className="mr-1.5 h-4 w-4" /> Reset
        </Button>
      </div>
      {rect && nat && mode === "custom" && (
        <p className="rounded-lg bg-re-soft px-3 py-2 text-[11px] text-re-muted" data-testid="crop-coords">
          Pixels (origineel {nat.w}×{nat.h}): x={Math.round(rect.x)}, y={Math.round(rect.y)}, breedte={Math.round(rect.width)}, hoogte={Math.round(rect.height)}
        </p>
      )}
    </div>
  );
}
